import { useLayoutEffect, useRef } from 'react';

// One line of text that shrinks its font size to fit the width it's given, instead of wrapping or
// being cut off. Set the full size with a text-* class on it; it scales down from there to
// `minScale`, and past that is clipped with an ellipsis.
function FitText({ as: Tag = 'span', children, className = '', minScale = 0.55 }) {
  const outerRef = useRef(null);
  const innerRef = useRef(null);

  useLayoutEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return undefined;

    let cancelled = false;
    const fit = () => {
      inner.style.fontSize = '';
      const available = outer.clientWidth;
      const needed = inner.getBoundingClientRect().width;
      if (available > 0 && needed > available) {
        // Text width scales with font size; the small margin absorbs rounding.
        inner.style.fontSize = `${Math.max((available / needed) * 0.98, minScale)}em`;
      }
    };

    fit();
    // Web fonts change the text width once they load.
    document.fonts?.ready.then(() => {
      if (!cancelled) fit();
    });

    let lastWidth = outer.clientWidth;
    const observer = new ResizeObserver(() => {
      if (outer.clientWidth === lastWidth) return;
      lastWidth = outer.clientWidth;
      fit();
    });
    observer.observe(outer);

    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [children, minScale]);

  return (
    <Tag ref={outerRef} className={`truncate ${className}`}>
      <span ref={innerRef}>{children}</span>
    </Tag>
  );
}

export default FitText;
